/**
 * Utility functions for handling and displaying user-friendly error messages
 */

/**
 * Transforms technical error messages into user-friendly ones
 */
export function getUserFriendlyErrorMessage(error: any): string {
  // Handle common error scenarios
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Extract error message from various error formats
  let message = '';
  if (typeof error === 'string') {
    message = error;
  } else if (error.response?.data?.detail) {
    message = error.response.data.detail;
  } else if (error.response?.data?.message) {
    message = error.response.data.message;
  } else if (error.message) {
    message = error.message;
  }

  // Transform technical messages to user-friendly ones
  const errorMappings: Record<string, string> = {
    // Network errors
    'Network Error': 'Unable to connect to the server. Please check your internet connection and try again.',
    'Request failed with status code 500': 'The server encountered an error. Please try again later.',
    'Request failed with status code 404': 'The requested resource was not found. It may have been moved or deleted.',
    'Request failed with status code 403': 'You don\'t have permission to access this resource.',
    'Request failed with status code 401': 'Your session has expired. Please refresh the page.',
    'Request failed with status code 400': 'Invalid request. Please check your input and try again.',
    
    // Session errors
    'Failed to create session': 'Unable to start a new session. Please refresh the page and try again.',
    'Session not found': 'Your session has expired. Please refresh the page to continue.',
    'Failed to load session': 'Unable to load your session. Please refresh the page.',
    
    // File errors
    'Failed to upload file': 'Unable to upload the file. Please check the file and try again.',
    'File too large': 'The file is too large. Please upload a file smaller than 100MB.',
    'Invalid file type': 'This file type is not supported. Please upload CSV, Excel, PDF, or Word documents.',
    'Empty file': 'The file appears to be empty. Please select a file with content.',
    'Failed to preview file': 'Unable to generate file preview. The file may be corrupted or in an unsupported format.',
    
    // Workflow errors
    'Failed to load workflows': 'Unable to load workflows. Please refresh the page.',
    'Failed to save workflow': 'Unable to save the workflow. Please check your input and try again.',
    'Failed to execute workflow': 'Unable to run the workflow. Please check your files and try again.',
    'Workflow not found': 'The workflow could not be found. It may have been deleted.',
    'Script execution failed': 'The workflow encountered an error during execution. Please check your input files and try again.',
    
    // Script errors
    'Failed to save script': 'Unable to save the script. Please try again.',
    'Script not found': 'The script could not be found.',
    'No script generated': 'No script was generated. Please provide more details in your request.',
    
    // General errors
    'Internal server error': 'Something went wrong on our end. Please try again later.',
    'Timeout': 'The operation took too long. Please try again with smaller files or simpler requests.',
    'Invalid input': 'Please check your input and try again.',
  };

  // Check for exact matches
  for (const [key, value] of Object.entries(errorMappings)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }

  // Handle specific error patterns
  if (message.includes('ECONNREFUSED')) {
    return 'Unable to connect to the server. Please ensure the server is running.';
  }

  if (message.includes('timeout') || message.includes('timed out')) {
    return 'The operation took too long. Please try again with smaller files or simpler requests.';
  }

  if (message.includes('permission') || message.includes('forbidden')) {
    return 'You don\'t have permission to perform this action.';
  }

  if (message.includes('not found') || message.includes('404')) {
    return 'The requested resource was not found.';
  }

  if (message.includes('already exists')) {
    return 'This item already exists. Please choose a different name.';
  }

  // If no specific mapping found, clean up the technical message
  if (message && message.length < 200) {
    // Remove technical details like stack traces, file paths, etc.
    return message
      .replace(/\s+at\s+.+/g, '') // Remove stack traces
      .replace(/\/.+\//g, '') // Remove file paths
      .replace(/Error:\s*/g, '') // Remove "Error:" prefix
      .trim() || 'An error occurred. Please try again.';
  }

  // Default fallback
  return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
}

/**
 * Formats error for display in UI components
 */
export function formatErrorForDisplay(error: any, context?: string): {
  title: string;
  message: string;
  actions?: string[];
} {
  const message = getUserFriendlyErrorMessage(error);
  
  // Determine title based on context
  let title = 'Error';
  if (context) {
    const contextTitles: Record<string, string> = {
      upload: 'Upload Failed',
      save: 'Save Failed',
      load: 'Loading Failed',
      execute: 'Execution Failed',
      network: 'Connection Error',
      validation: 'Validation Error',
    };
    title = contextTitles[context] || 'Error';
  }

  // Suggest actions based on error type
  const actions: string[] = [];
  if (message.includes('internet connection') || message.includes('server')) {
    actions.push('Check your internet connection');
    actions.push('Refresh the page');
  } else if (message.includes('session')) {
    actions.push('Refresh the page');
  } else if (message.includes('file')) {
    actions.push('Check your file format and size');
    actions.push('Try a different file');
  }

  return { title, message, actions };
}